import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { authApiService } from '../services/authApiService'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../services/BaseApiService'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'

export default function AcceptInvitePage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const token = params.get('token') ?? ''

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(true)

  useEffect(() => {
    if (!token) { setError('Missing invite token.'); setValidating(false); return }
    authApiService.validateInvite(token)
      .then(r => { setEmail(r.email) })
      .catch(() => setError('This invite link is invalid or has expired.'))
      .finally(() => setValidating(false))
  }, [token])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (password !== confirm) { setError('Passwords do not match.'); return }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    setError(''); setLoading(true)
    try {
      await authApiService.acceptInvite(token, password, firstName, lastName)
      // The accept call signs the user in; load that session before routing, or the app still sees
      // "signed out" and bounces to /login (workspaces follow the session automatically). Auditors go
      // to their portal, everyone else to the app.
      await refresh()
      const me = await authApiService.me()
      navigate(me?.isAuditor ? '/audit' : '/app/dashboard', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to accept invite.')
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    fontSize: fontSize.base,
    color: colors.text,
    boxSizing: 'border-box',
    outline: 'none',
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: colors.background,
    }}>
      <div style={{
        width: 420,
        maxWidth: '90vw',
        background: colors.surface,
        borderRadius: radius.lg,
        boxShadow: shadow.lg,
        padding: spacing.xl,
      }}>
        <h1 style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, marginBottom: spacing.xs, marginTop: 0 }}>
          Accept Invitation
        </h1>

        {validating ? (
          <p style={{ color: colors.textSecondary }}>Validating invite…</p>
        ) : error && !email ? (
          <div style={{ color: colors.error }}>{error}</div>
        ) : (
          <>
            {email && (
              <p style={{ color: colors.textSecondary, fontSize: fontSize.sm, marginBottom: spacing.lg }}>
                Setting up your account for <strong>{email}</strong>
              </p>
            )}
            {error && (
              <div style={{
                background: colors.errorLight, color: colors.error,
                border: `1px solid #fecaca`, borderRadius: radius.sm,
                padding: `${spacing.sm} ${spacing.md}`,
                fontSize: fontSize.sm, marginBottom: spacing.md,
              }}>{error}</div>
            )}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.sm }}>
                <div>
                  <label style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 500, marginBottom: spacing.xs }}>
                    First Name
                  </label>
                  <input
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 500, marginBottom: spacing.xs }}>
                    Last Name
                  </label>
                  <input
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 500, marginBottom: spacing.xs }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={8}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 500, marginBottom: spacing.xs }}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  style={inputStyle}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '10px 0',
                  background: colors.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: radius.sm,
                  fontSize: fontSize.base,
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Setting up account…' : 'Create Account & Sign In'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
